import FarmShareBG from '../../assets/FarmshareBG.jpg';
import { Navbar } from '../../components/Navbar';

import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReservationFilter } from '../../components/ReservationFilter';
import { UserReservationCard } from '../../components/UserReservationCard';
import { EditReservationModal } from '../../components/EditReservationModal';
import { UserCancelReservationModal } from '../../components/UserCancelReservationModal';
import { getSingleEquipment } from '../../redux/slices/equipmentsSlice';
import { getMyReservations } from '../../redux/slices/myselfSlice';
import { LoadingModal } from '../../components/LoadingModal';
import { getEquipmentTranslation } from '../../utils/equipmentTranslation';

const UserReservations = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const reservations = useSelector((state) => state.myself.reservations);
  const loading = useSelector((state) => state.myself.loading);
  const error = useSelector((state) => state.reservations.error);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [cancelModal, setCancelModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [showEditReservationModal, setShowEditReservationModal] = useState(false);
  const [equipments, setEquipments] = useState({});
  const [selectedReservation, setSelectedReservation] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getMyReservations());
  }, [dispatch]);

  useEffect(() => {
    if (reservations && Array.isArray(reservations)) {
      setFilteredReservations(reservations.filter((res) => res));
    }
  }, [reservations]);

  const filterReservations = (key) => {
    if (key === 'all') {
      setFilteredReservations(reservations.filter((res) => res));
    } else {
      let filtered = reservations.filter(
        (reservation) => reservation && reservation.reservationStatus === key
      );
      setFilteredReservations(filtered);
    }
  };

  useEffect(() => {
    const fetchEquipments = async () => {
      const equipIds = [
        ...new Set(
          reservations
            .filter((reservation) => reservation && reservation.equipmentTypeId)
            .map((reservation) => reservation.equipmentTypeId)
        ),
      ];

      for (const equipId of equipIds) {
        if (!equipments[equipId]) {
          try {
            const action = await dispatch(getSingleEquipment(equipId));

            if (getSingleEquipment.fulfilled.match(action)) {
              setEquipments((prevEquips) => ({
                ...prevEquips,
                [equipId]: action.payload,
              }));
            } else {
              console.error(`Failed to fetch equipment with ID ${equipId}:`, action.payload);
            }
          } catch (err) {
            console.error(`Error fetching equipment with ID ${equipId}:`, err);
          }
        }
      }
    };

    if (equipments && Object.keys(equipments).length === 0) {
      fetchEquipments();
    }
    filterReservations(currentFilter);
  }, [dispatch, reservations]);

  return (
    <div className="bg-sky-200 min-h-screen flex flex-col">
      <Navbar
        src={FarmShareBG}
        pageName={t('farmshare.userReservations.navbar.pageName')}
        pageText={t('farmshare.userReservations.navbar.pageText')}
      />
      <ReservationFilter
        filterReservations={filterReservations}
        setCurrentFilter={setCurrentFilter}
        currentFilter={currentFilter}
      />

      {loading && (
        <LoadingModal
          loading={loading}
          error={error}
          message={t('farmshare.userReservations.loadingModal.cancelling')}
        />
      )}

      {cancelModal && (
        <UserCancelReservationModal
          setCancelModal={setCancelModal}
          selectedReservation={selectedReservation}
          setSelectedReservation={setSelectedReservation}
          onReservationUpdated={() => dispatch(getMyReservations())}
        />
      )}

      {showEditReservationModal && editModal && (
        <EditReservationModal
          setShowEditReservationModal={setShowEditReservationModal}
          reservation={editModal}
          equipment={editModal.equipmentTypeId ? equipments[editModal.equipmentTypeId] : null}
          renterName="Current User"
          onReservationUpdated={() => dispatch(getMyReservations())}
        />
      )}

      <div className="flex-1 flex flex-col items-center">
        {filteredReservations && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 w-full max-w-6xl mx-auto p-4">
            {filteredReservations &&
              // ensure that filteredReservations never contains any undefined items by adding a filter:
              filteredReservations
                .filter((reservation) => reservation)
                .map((reservation) => {
                  const equip = reservation.equipmentTypeId
                    ? equipments[reservation.equipmentTypeId]
                    : null;
                  const equipName = equip
                    ? getEquipmentTranslation(equip, 'name', currentLang)
                    : t('farmshare.userReservations.cards.equipmentNameLoading');
                  return (
                    <UserReservationCard
                      key={reservation._id}
                      startDate={reservation.startDate}
                      endDate={reservation.endDate}
                      status={reservation.reservationStatus}
                      equipmentName={equipName}
                      setCancelModal={setCancelModal}
                      resId={reservation._id}
                      reservation={reservation}
                      setSelectedReservation={setSelectedReservation}
                      setEditModal={setEditModal}
                      setShowEditReservationModal={setShowEditReservationModal}
                      equipment={equip}
                    />
                  );
                })}
          </div>
        )}

        {loading && reservations.length === 0 && (
          <div className="flex justify-center items-center w-full h-full">
            <p className="text-gray-700">{t('farmshare.userReservations.list.loading')}</p>
          </div>
        )}

        {error && !reservations && (
          <div className="flex justify-center items-center w-full h-full">
            <p className="text-red-600 font-bold">
              {error} - {t('farmshare.userReservations.list.errorTryAgain')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export { UserReservations };
